import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { stageLabel, SOURCE_LABELS } from "./shared";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Clock, Target, Users } from "lucide-react";

const TOOLTIP_STYLE = { backgroundColor: "#0a0a0a", border: "1px solid rgba(255,255,255,0.15)", fontSize: 12 };

export default function CrmAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/crm/analytics").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-10 text-center text-ink-400 text-sm">Elaborazione analytics...</div>;
    if (!data) return <div className="p-10 text-center text-ink-400 text-sm">Dati non disponibili.</div>;
    const k = data.kpi || {};

    return (
        <div data-testid="crm-analytics-tab" className="space-y-10">
            {/* KPI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi icon={<Users size={15} />} label="Lead totali" value={k.total_leads} />
                <Kpi icon={<Target size={15} />} label="Lead → Chiusura" value={`${k.lead_to_close_pct}%`} highlight />
                <Kpi icon={<Clock size={15} />} label="Tempo medio vendita" value={k.avg_sale_days != null ? `${k.avg_sale_days} gg` : "—"} sub={k.sold_count ? `su ${k.sold_count} chiusure` : "nessuna chiusura"} />
                <Kpi icon={<TrendingUp size={15} />} label="Clienti attivi" value={k.clients} />
            </div>

            {/* Monthly trend */}
            <Section title="Andamento mensile" subtitle="Lead ricevuti, visite e vendite/affitti conclusi negli ultimi 12 mesi.">
                <div data-testid="chart-monthly" style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                        <LineChart data={data.monthly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                            <XAxis dataKey="month" tick={{ fill: "#71717a", fontSize: 10 }} />
                            <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="leads" name="Lead" stroke="#a78bfa" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="visite" name="Visite" stroke="#60a5fa" strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="vendite" name="Vendite/Affitti" stroke="#D4AF6A" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Lead sources */}
                <Section title="Origine dei lead" subtitle="Da dove arrivano i tuoi contatti.">
                    <div data-testid="chart-sources" style={{ width: "100%", height: 220 }}>
                        <ResponsiveContainer>
                            <BarChart data={(data.sources || []).map((s) => ({ ...s, label: SOURCE_LABELS[s.source] || s.source }))} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                                <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 10 }} />
                                <YAxis tick={{ fill: "#71717a", fontSize: 10 }} allowDecimals={false} />
                                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                                <Bar dataKey="count" name="Lead" fill="#a78bfa" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Section>

                {/* Funnel */}
                <Section title="Funnel per fase" subtitle="Distribuzione dei lead nella pipeline.">
                    <div data-testid="chart-funnel" className="space-y-2 pt-2">
                        {(data.funnel || []).map((f) => {
                            const max = Math.max(1, ...data.funnel.map((x) => x.count));
                            return (
                                <div key={f.stage} className="flex items-center gap-3 text-xs">
                                    <span className="w-36 text-ink-400 shrink-0">{stageLabel(f.stage)}</span>
                                    <div className="flex-1 h-4 bg-ink-950 relative">
                                        <div className={`absolute inset-y-0 left-0 ${f.stage === "conclusa" ? "bg-green-500/70" : f.stage === "persa" ? "bg-red-500/50" : "bg-purple-500/60"}`} style={{ width: `${(f.count / max) * 100}%` }} />
                                    </div>
                                    <span className="w-8 text-right text-ink-100">{f.count}</span>
                                </div>
                            );
                        })}
                    </div>
                </Section>
            </div>

            {/* Agents performance */}
            <Section title="Performance agenti" subtitle="Lead gestiti, clienti, trattative e chiusure per agente.">
                <div data-testid="agents-performance" className="border border-white/10">
                    <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-ink-400 bg-white/5 px-4 py-2.5">
                        <div className="col-span-4">Agente</div>
                        <div className="col-span-2 text-center">Lead</div>
                        <div className="col-span-2 text-center">Clienti</div>
                        <div className="col-span-2 text-center">In trattativa</div>
                        <div className="col-span-2 text-center">Conversione</div>
                    </div>
                    {(data.agents || []).map((a) => (
                        <div key={a.agent} className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 text-sm border-t border-white/5">
                            <div className={`col-span-4 truncate ${a.agent === "Non assegnato" ? "text-ink-500 italic" : "text-ink-50"}`}>{a.agent}</div>
                            <div className="col-span-2 text-center text-ink-100">{a.leads}</div>
                            <div className="col-span-2 text-center text-ink-100">{a.clients}</div>
                            <div className="col-span-2 text-center text-ink-100">{a.trattativa}</div>
                            <div className="col-span-2 text-center"><span className={`px-2 py-0.5 text-xs ${a.conversion_pct >= 20 ? "bg-green-900/30 text-green-300" : "bg-ink-900 text-ink-400"}`}>{a.conversion_pct}%</span></div>
                        </div>
                    ))}
                    {(data.agents || []).length === 0 && <div className="p-6 text-center text-xs text-ink-500">Nessun dato. Assegna gli agenti ai lead dal tab CRM.</div>}
                </div>
            </Section>

            {/* Yearly */}
            {(data.yearly || []).length > 0 && (
                <Section title="Andamento annuale" subtitle="Lead e chiusure per anno.">
                    <div data-testid="yearly-table" className="flex gap-4 flex-wrap">
                        {data.yearly.map((y) => (
                            <div key={y.year} className="border border-white/10 bg-ink-900/40 px-6 py-4 text-center">
                                <div className="font-serif text-2xl text-gold">{y.year}</div>
                                <div className="text-xs text-ink-400 mt-1">{y.leads} lead · {y.vendite} chiusure</div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}

function Kpi({ icon, label, value, sub, highlight }) {
    return (
        <div className={`p-5 border ${highlight ? "border-gold/40 bg-gold/5" : "border-white/10 bg-ink-900/40"}`}>
            <div className="flex items-center gap-2 text-ink-400 text-[10px] uppercase tracking-wider">{icon}{label}</div>
            <div className="font-serif text-3xl text-ink-50 mt-1.5">{value ?? "—"}</div>
            {sub && <div className="text-[10px] text-ink-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="border border-white/10 bg-ink-900/30 p-6">
            <h3 className="font-serif text-xl text-ink-50">{title}</h3>
            <p className="text-xs text-ink-400 mt-1 mb-4">{subtitle}</p>
            {children}
        </div>
    );
}
